import { VideoLayout } from '@core/types';
import { Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { percentToTimestamp, useTimelineDrag } from '../../../hooks/useTimelineDrag';
import { Timeline, VideoLayoutTimelineEvent } from '../../../parser';

export const VideoLayoutTrack = ({
    timeline,
    duration,
    isActive,
    onClick
}: {
    timeline: Timeline;
    duration: Duration;
    isActive: boolean;
    onClick: (event: VideoLayoutTimelineEvent) => void;
}) => {
    const [layouts, setLayouts] = useState<VideoLayoutTimelineEvent[]>([]);
    const { t } = useTranslation();

    const videoLayoutMap: Record<keyof typeof VideoLayout, string> = {
        Default: t('Editor.VideoLayout.Item.Default'),
        VideoOnly: t('Editor.VideoLayout.Item.Default'),
        PresentationWithVideoLarge: t('Editor.VideoLayout.Item.PresentationWithVideoLarge'),
        PresentationWithVideoSmall: t('Editor.VideoLayout.Item.PresentationWithVideoSmall'),
        DesktopSharing: t('Editor.VideoLayout.Item.DesktopSharing'),
        Classic: t('Editor.VideoLayout.Item.Default'),
        DesktopSharingWithVideo: t('Editor.VideoLayout.Item.DesktopSharingWithVideo')
    };

    useEffect(() => {
        if (!timeline?.videoLayouts || duration.toMillis() === 0) {
            setLayouts([]);
            return;
        }

        setLayouts(timeline.videoLayouts);
    }, [timeline?.videoLayouts, duration]);

    const drag = useTimelineDrag<VideoLayoutTimelineEvent>(
        (videoLayout, position) => {
            // Video time works in seconds, so we need to round the timestamp to the nearest second
            videoLayout.timestamp = percentToTimestamp(duration, position);
            // Check if there are two video layouts with the same timestamp
            const duplicate = timeline.videoLayouts.find((v) => v.timestamp.equals(videoLayout.timestamp) && v !== videoLayout);
            if (!!duplicate) {
                if (videoLayout.timestamp.as('seconds') > 0) {
                    videoLayout.timestamp = videoLayout.timestamp.minus({ seconds: 1 });
                } else {
                    videoLayout.timestamp = videoLayout.timestamp.plus({ seconds: 1 });
                }
            }
            setLayouts([...timeline.videoLayouts.sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis())]);
        },
        (videoLayout) => {
            videoLayout.changed = true;
            timeline.changed = true;
            timeline.videoLayouts = [...timeline.videoLayouts];
        }
    );

    const onDragStart = (e: React.MouseEvent<HTMLButtonElement>, videoLayout: VideoLayoutTimelineEvent) =>
        drag.begin(e, videoLayout, (videoLayout.timestamp.toMillis() / duration.toMillis()) * 100);

    return (
        <div className={`track track-video-layout ${isActive ? 'track-active' : ''} row-start-2`}>
            {layouts.map((videoLayout, index) => {
                const layoutKey = VideoLayout[videoLayout.layout];
                let width = 0;
                if (index === layouts.length - 1) {
                    width = ((duration.toMillis() - layouts[index].timestamp.toMillis()) / duration.toMillis()) * 100;
                } else {
                    width = ((layouts[index + 1].timestamp.toMillis() - layouts[index].timestamp.toMillis()) / duration.toMillis()) * 100;
                }

                return (
                    <div
                        key={index}
                        style={{ left: `${(videoLayout.timestamp.toMillis() / duration.toMillis()) * 100}%`, width: `${width}%` }}
                        onClick={() => onClick(videoLayout)}
                        className={videoLayout.changed ? 'changed' : ''}
                    >
                        {/** Draggen disabled (voor nu): <button onMouseDown={(event) => onDragStart(event, videoLayout)}></button> */}
                        {/** Misschien een wireframe plaatje van de actieve layout? */}
                        <span>{videoLayoutMap[layoutKey as keyof typeof VideoLayout] ?? t('Editor.VideoLayout.Item.UnknownLayout')}</span>
                    </div>
                );
            })}
        </div>
    );
};
